import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Calendar } from "lucide-react";
import api from "../api/client";
import GSCOverviewCards from "./GSCOverviewCards";
import GSCChart from "./GSCChart";
import GSCPagesTable from "./GSCPagesTable";
import GSCQueriesTable from "./GSCQueriesTable";
import CTRInsightsList from "./CTRInsightsList";

export default function SearchPerformance({ selectedSite }) {
  const [overview, setOverview] = useState(null);
  const [chart, setChart] = useState([]);
  const [pages, setPages] = useState([]);
  const [queries, setQueries] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [period, setPeriod] = useState(7);
  const [tab, setTab] = useState("insights");
  const [engine, setEngine] = useState("google");
  const [bingConnected, setBingConnected] = useState(false);
  const [compareData, setCompareData] = useState(null);

  const fetchData = useCallback(() => {
    if (!selectedSite) return;
    setLoading(true);

    // Check Bing connection status
    api
      .get("/websites/" + selectedSite.id + "/bing/connection/")
      .then((r) => setBingConnected(r.data.connected))
      .catch(() => setBingConnected(false));

    if (engine === "combined") {
      // Fetch comparison data
      Promise.all([
        api.get("/websites/" + selectedSite.id + "/gsc/overview/", { params: { days: period } }),
        api.get("/websites/" + selectedSite.id + "/gsc/chart/", { params: { days: 30 } }),
        api.get("/websites/" + selectedSite.id + "/bing/compare/", { params: { days: period } }),
        api.get("/websites/" + selectedSite.id + "/gsc/insights/"),
      ])
        .then(([overviewRes, chartRes, compareRes, insightsRes]) => {
          setOverview(overviewRes.data);
          setChart(chartRes.data);
          setCompareData(compareRes.data);
          setInsights(insightsRes.data.results || insightsRes.data);
          setPages([]);
          setQueries([]);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }

    const prefix = engine === "bing" ? "/bing" : "/gsc";
    const base = "/websites/" + selectedSite.id;

    const requests = [
      api.get(base + prefix + "/overview/", { params: { days: period } }),
    ];

    if (engine === "google") {
      requests.push(api.get(base + "/gsc/chart/", { params: { days: 30 } }));
    }

    requests.push(api.get(base + prefix + "/pages/", { params: { days: period } }));
    requests.push(api.get(base + prefix + "/queries/", { params: { days: period } }));
    requests.push(api.get(base + "/gsc/insights/", { params: engine === "bing" ? { source: "bing" } : {} }));

    Promise.all(requests)
      .then((results) => {
        let idx = 0;
        setOverview(results[idx++].data);
        if (engine === "google") {
          setChart(results[idx++].data);
        } else {
          setChart([]);
        }
        setPages(results[idx].data.results || results[idx].data);
        idx++;
        setQueries(results[idx].data.results || results[idx].data);
        idx++;
        setInsights(results[idx].data.results || results[idx].data);
        setCompareData(null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite, period, engine]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const triggerSync = () => {
    if (!selectedSite) return;
    setSyncing(true);
    const endpoint =
      engine === "bing"
        ? "/websites/" + selectedSite.id + "/bing/connection/"
        : "/websites/" + selectedSite.id + "/gsc/sync/";
    api
      .post(endpoint)
      .then(() => {})
      .catch(() => {})
      .finally(() => setSyncing(false));
  };

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view Search Performance
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Search Performance</h2>
        <div className="flex items-center gap-3">
          {/* Engine toggle */}
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
            {[
              { value: "google", label: "Google" },
              { value: "bing", label: "Bing" },
              { value: "combined", label: "Combined" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setEngine(opt.value)}
                disabled={opt.value !== "google" && !bingConnected}
                className={
                  "px-3 py-1.5 text-xs rounded-md transition-colors " +
                  (engine === opt.value
                    ? "bg-copper/20 text-copper"
                    : opt.value !== "google" && !bingConnected
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 hover:text-white")
                }
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Period selector */}
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
            {[
              { value: 7, label: "7d" },
              { value: 14, label: "14d" },
              { value: 28, label: "28d" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors " +
                  (period === opt.value
                    ? "bg-copper/20 text-copper"
                    : "text-gray-400")
                }
              >
                <Calendar size={12} />
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={triggerSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={syncing ? "animate-spin" : ""}
            />
            {syncing ? "Syncing..." : engine === "bing" ? "Sync Bing" : "Sync GSC"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 bg-dark-300 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-dark-300 rounded-xl" />
          <div className="h-48 bg-dark-300 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Combined comparison view */}
          {engine === "combined" && compareData && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
              <h3 className="text-sm font-semibold mb-4">
                Google vs Bing — {period} Day Comparison
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-xs text-gray-400 mb-2">Google</div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">
                        Impressions
                      </span>
                      <span className="font-medium">
                        {(
                          compareData.summary?.google_impressions || 0
                        ).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Clicks</span>
                      <span className="font-medium">
                        {(
                          compareData.summary?.google_clicks || 0
                        ).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">
                        Avg Position
                      </span>
                      <span className="font-medium">
                        {compareData.summary?.google_avg_position || "—"}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-2">Bing</div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">
                        Impressions
                      </span>
                      <span className="font-medium">
                        {(
                          compareData.summary?.bing_impressions || 0
                        ).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Clicks</span>
                      <span className="font-medium">
                        {(
                          compareData.summary?.bing_clicks || 0
                        ).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">
                        Avg Position
                      </span>
                      <span className="font-medium">
                        {compareData.summary?.bing_avg_position || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-dark-50 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-lg font-semibold text-copper">
                    {compareData.summary?.shared_queries || 0}
                  </div>
                  <div className="text-xs text-gray-400">Shared Queries</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-blue-400">
                    {compareData.summary?.google_only_queries || 0}
                  </div>
                  <div className="text-xs text-gray-400">
                    Google-Only Queries
                  </div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-green-400">
                    {compareData.summary?.bing_only_queries || 0}
                  </div>
                  <div className="text-xs text-gray-400">
                    Bing-Only Queries
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Standard view */}
          {engine !== "combined" && (
            <>
              <GSCOverviewCards data={overview} />
              {chart.length > 0 && <GSCChart data={chart} />}
            </>
          )}

          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5 w-fit">
            {[
              { id: "insights", label: "Insights" },
              { id: "pages", label: "Pages" },
              { id: "queries", label: "Queries" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={
                  "px-4 py-1.5 text-xs rounded-md transition-colors " +
                  (tab === t.id
                    ? "bg-copper/20 text-copper"
                    : "text-gray-400 hover:text-white")
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "insights" && (
            <CTRInsightsList
              insights={insights}
              siteId={selectedSite.id}
              onUpdate={fetchData}
            />
          )}
          {tab === "pages" && <GSCPagesTable data={pages} />}
          {tab === "queries" && <GSCQueriesTable data={queries} />}
        </>
      )}
    </div>
  );
}
